import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Maximize, Tag, Check, MessageCircle, CalendarCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useConfig, waLink } from "@/context/ConfigContext";
import { goToEnquiry } from "@/lib/scroll";
import { IMAGES } from "@/lib/api";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const config = useConfig();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    setProperty(null);
    setActive(0);
    api
      .get(`/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 pt-[72px]" data-testid="property-not-found">
        <p className="font-display text-3xl text-navy">Property not found</p>
        <Link to="/properties" className="text-sm font-semibold text-gold-dark underline">
          Back to all properties
        </Link>
      </main>
    );
  }

  if (!property) {
    return (
      <main className="min-h-screen pt-[72px]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="aspect-[16/9] animate-pulse rounded-xl bg-gray-100" />
        </div>
      </main>
    );
  }

  const p = property;
  const images = p.images?.length ? p.images : [IMAGES.hero];

  return (
    <main data-testid="property-detail-page" className="pb-24 pt-[72px] md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/properties"
          data-testid="back-to-properties-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Properties
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="overflow-hidden rounded-xl">
              <img
                src={images[active]}
                alt={`${p.title} - ${p.property_type} plot in ${p.location}, Nagpur`}
                className="aspect-[16/10] w-full object-cover"
                data-testid="property-main-image"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`h-20 w-28 shrink-0 overflow-hidden rounded-sm border-2 transition-colors ${
                      i === active ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-10">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-sm bg-navy px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                  {p.property_type}
                </span>
                <span
                  data-testid="detail-status-badge"
                  className={`rounded-sm px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${
                    p.status === "Available" ? "bg-gold text-navy" : "bg-gray-200 text-navy"
                  }`}
                >
                  {p.status}
                </span>
                {p.is_sample && (
                  <span className="rounded-sm bg-gray-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Sample Listing
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-navy sm:text-5xl">{p.title}</h1>
              <p className="mt-3 flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 text-gold-dark" aria-hidden="true" />
                {p.location}
              </p>

              <p className="mt-7 text-base leading-relaxed text-muted-foreground md:text-lg">{p.description}</p>

              {p.location_advantage && (
                <div className="mt-8 rounded-xl border-l-4 border-gold bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold-dark">Location Advantage</p>
                  <p className="mt-2 text-sm leading-relaxed text-navy md:text-base">{p.location_advantage}</p>
                </div>
              )}

              {p.amenities?.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-display text-2xl font-semibold text-navy">Features &amp; Amenities</h2>
                  <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                    {p.amenities.map((a) => (
                      <li key={a} className="flex items-center gap-3 rounded-sm border border-gray-100 bg-white px-4 py-3 text-sm text-navy">
                        <Check className="h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10">
                <h2 className="font-display text-2xl font-semibold text-navy">Location &amp; Connectivity</h2>
                <div className="relative mt-5 overflow-hidden rounded-xl">
                  <img src={IMAGES.map} alt={`Map placeholder showing ${p.location} area`} loading="lazy" className="aspect-[16/7] w-full object-cover" />
                  <div className="absolute inset-0 bg-navy/40" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="rounded-sm bg-navy/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white backdrop-blur">
                      Map placeholder &mdash; {p.location_zone}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-xl border border-gray-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.06)] lg:sticky lg:top-24">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-navy/50">Price</p>
              <p data-testid="detail-price" className="mt-1 font-display text-3xl font-bold text-navy">
                {p.price_label}
              </p>
              <dl className="mt-6 space-y-4 border-t border-gray-100 pt-6 text-sm">
                <div className="flex items-center justify-between gap-4">
                  <dt className="flex items-center gap-2 text-muted-foreground">
                    <Maximize className="h-4 w-4 text-gold-dark" aria-hidden="true" /> Plot Size
                  </dt>
                  <dd className="font-semibold text-navy">{p.plot_size || "On request"}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="flex items-center gap-2 text-muted-foreground">
                    <Tag className="h-4 w-4 text-gold-dark" aria-hidden="true" /> Type
                  </dt>
                  <dd className="font-semibold text-navy">{p.property_type}</dd>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <dt className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4 text-gold-dark" aria-hidden="true" /> Zone
                  </dt>
                  <dd className="font-semibold text-navy">{p.location_zone}</dd>
                </div>
              </dl>
              <button
                data-testid="detail-enquire-button"
                onClick={() => goToEnquiry(navigate, p.title)}
                className="mt-7 w-full rounded-sm bg-gold px-6 py-4 text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-light"
              >
                Enquire Now
              </button>
              <button
                data-testid="detail-site-visit-button"
                onClick={() => goToEnquiry(navigate, `${p.title} (Site Visit Request)`)}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-navy/20 px-6 py-4 text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:border-navy hover:bg-navy hover:text-white"
              >
                <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                Request Site Visit
              </button>
              <a
                href={waLink(config, `Hello, I'm interested in "${p.title}" (${p.location}). Please share details.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-button"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm bg-[#25D366] px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-transform duration-200 hover:scale-[1.02]"
              >
                <MessageCircle className="h-4 w-4" aria-hidden="true" />
                WhatsApp
              </a>
            </div>
          </aside>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 p-3 backdrop-blur-xl md:hidden" data-testid="mobile-sticky-enquire-bar">
        <button
          data-testid="mobile-sticky-enquire-button"
          onClick={() => goToEnquiry(navigate, p.title)}
          className="w-full rounded-sm bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-navy"
        >
          Enquire Now
        </button>
      </div>
    </main>
  );
}
